import { useState, useEffect, useCallback } from 'react'
import { Bell, Loader, Pencil, Plus, Check, Type, Image as ImageIcon, Link as LinkIcon } from 'lucide-react'
import { NewsroomBlock } from './components/NewsroomBlock'
import { NotificationSettingsModal } from './components/NotificationSettingsModal'
import { NewsroomComposer, useIsNewsroomModerator } from './NewsroomComposer'

type ComposeType = 'text' | 'photo' | 'link'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

interface Block {
  id: string
  block_type: string
  content: any
  layout_config: {
    gridSpan: number
    customHeight: number | null
    mobileSpan: number
  }
  s3_key?: string
  file_url?: string
  thumbnail_url?: string
  like_count: number
  user_liked: boolean
  options?: any[]
  user_voted_option?: string
  is_pinned?: boolean
}

export default function NewsroomPage() {
  const [blocks, setBlocks] = useState<Block[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [offset, setOffset] = useState(0)
  const [showNotificationSettings, setShowNotificationSettings] = useState(false)
  const [showEditPanel, setShowEditPanel] = useState(false)
  const [composeIntent, setComposeIntent] = useState<ComposeType | null>(null)
  const [editMode, setEditMode] = useState(false)
  const isModerator = useIsNewsroomModerator()

  // Open the composer straight into the compose form (Facebook-style entry).
  const openComposer = useCallback((type: ComposeType = 'text') => {
    setComposeIntent(type)
    setShowEditPanel(true)
  }, [])
  // Open the panel on its "your posts" list view (header button).
  const openPanel = useCallback(() => {
    setComposeIntent(null)
    setShowEditPanel(true)
  }, [])
  const closePanel = useCallback(() => {
    setShowEditPanel(false)
    setComposeIntent(null)
  }, [])

  const limit = 20

  // Get auth token
  const getAuthToken = useCallback(() => {
    return localStorage.getItem('auth_token')
  }, [])

  const authHeaders = useCallback((): Record<string, string> => {
    const token = localStorage.getItem('auth_token')
    return token ? { Authorization: `Bearer ${token}` } : {}
  }, [])

  // ---- In-place moderator editing (pin / edit / delete) ----
  const togglePin = useCallback(async (id: string, pinned: boolean) => {
    setBlocks(prev => {
      const updated = prev.map(b => b.id === id ? { ...b, is_pinned: pinned } : b)
      // Pinned blocks float to the top, mirroring the public feed ordering.
      return [...updated].sort((a, b) => (b.is_pinned ? 1 : 0) - (a.is_pinned ? 1 : 0))
    })
    await fetch(`${API_URL}/api/newsroom/moderator/blocks/${id}/pin`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ pinned }),
    }).catch(() => {})
  }, [authHeaders])

  const deleteBlock = useCallback(async (id: string) => {
    setBlocks(prev => prev.filter(b => b.id !== id))
    await fetch(`${API_URL}/api/newsroom/moderator/blocks/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    }).catch(() => {})
  }, [authHeaders])

  const saveBlockText = useCallback(async (id: string, text: string) => {
    setBlocks(prev => prev.map(b => b.id === id ? { ...b, content: { ...b.content, text } } : b))
    await fetch(`${API_URL}/api/newsroom/moderator/blocks/${id}`, {
      method: 'PATCH',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ content: { text } }),
    }).catch(() => {})
  }, [authHeaders])

  // Load blocks
  const loadBlocks = useCallback(async (reset = false) => {
    try {
      setIsLoading(true)

      const currentOffset = reset ? 0 : offset
      const token = getAuthToken()

      const headers: Record<string, string> = {}
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const response = await fetch(
        `${API_URL}/api/newsroom/blocks?limit=${limit}&offset=${currentOffset}`,
        { headers }
      )

      if (!response.ok) {
        throw new Error('Failed to load newsroom')
      }

      const result = await response.json()

      if (reset) {
        setBlocks(result.data)
        setOffset(limit)
      } else {
        setBlocks(prev => [...prev, ...result.data])
        setOffset(prev => prev + limit)
      }

      setHasMore(result.pagination.hasMore)
    } catch (error) {
      console.error('Load blocks error:', error)
    } finally {
      setIsLoading(false)
    }
  }, [offset, getAuthToken])

  // Initial load
  useEffect(() => {
    loadBlocks(true)
  }, [])

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (isLoading || !hasMore) return

    const scrollHeight = document.documentElement.scrollHeight
    const scrollTop = document.documentElement.scrollTop
    const clientHeight = window.innerHeight

    if (scrollTop + clientHeight >= scrollHeight - 500) {
      loadBlocks()
    }
  }, [isLoading, hasMore, loadBlocks])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Updates & News</h1>
            </div>

            <div className="flex items-center gap-2">
              {/* Open to every subscriber — the newsroom route is subscription-gated,
                  so anyone viewing it can submit a post for review. */}
              <button
                onClick={openPanel}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-pink-500 hover:bg-pink-600 text-white text-sm font-medium rounded-lg transition-colors"
                aria-label="Add a post"
              >
                <Plus size={16} />
                Post
              </button>
              {isModerator && (
                <button
                  onClick={() => setEditMode(m => !m)}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    editMode ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-gray-900 hover:bg-gray-800 text-white'
                  }`}
                  aria-label={editMode ? 'Done editing' : 'Edit newsroom'}
                >
                  {editMode ? <><Check size={16} /> Done</> : <><Pencil size={16} /> Edit</>}
                </button>
              )}
              <button
                onClick={() => setShowNotificationSettings(true)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Notification settings"
              >
                <Bell size={20} className="text-gray-600" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Blocks Grid */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        {/* Facebook-style composer entry — the inviting "share something" hook */}
        <ComposerEntry onOpen={openComposer} className="mb-4" />

        {blocks.length === 0 && !isLoading ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <p className="text-gray-600">No updates yet. Check back soon!</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {editMode && (
              <div className="text-center text-sm text-gray-500 bg-white border border-gray-200 rounded-xl py-2.5">
                <span className="font-medium text-gray-700">Edit mode</span> — pin, edit or remove any post. Tap <span className="font-medium text-green-600">Done</span> when finished.
              </div>
            )}
            {blocks.map((block) => (
              <NewsroomBlock
                key={block.id}
                block={block}
                editMode={editMode && !!isModerator}
                onTogglePin={togglePin}
                onDelete={deleteBlock}
                onSaveText={saveBlockText}
              />
            ))}
          </div>
        )}

        {/* Loading indicator */}
        {isLoading && (
          <div className="py-8 flex justify-center">
            <div className="flex flex-col items-center gap-3">
              <Loader className="animate-spin text-gray-400" size={32} />
              <p className="text-sm text-gray-600">Loading more updates...</p>
            </div>
          </div>
        )}

        {/* End of content */}
        {!hasMore && blocks.length > 0 && (
          <div className="py-8 text-center">
            <p className="text-sm text-gray-500">You're all caught up!</p>
          </div>
        )}
      </div>

      {/* Notification Settings Modal */}
      {showNotificationSettings && (
        <NotificationSettingsModal
          onClose={() => setShowNotificationSettings(false)}
        />
      )}

      {/* Composer panel — opened from the header Post button or the feed entry card */}
      <NewsroomComposer
        open={showEditPanel}
        onClose={closePanel}
        startCompose={composeIntent !== null}
        initialType={composeIntent ?? 'text'}
      />
    </div>
  )
}

/**
 * Prominent, inviting composer entry pinned to the top of the feed — reads like
 * Facebook's "What's on your mind?" box. Whole pill opens the composer; the
 * Text / Photo / Link chips open it pre-set to that post type.
 */
function ComposerEntry({ onOpen, className = '' }: { onOpen: (type: ComposeType) => void; className?: string }) {
  return (
    <div className={`bg-white rounded-2xl border border-gray-200 shadow-sm p-3 sm:p-4 ${className}`}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-400 to-pink-600 flex items-center justify-center text-white font-semibold shadow-sm flex-shrink-0">
          C
        </div>
        <button
          data-testid="composer-entry"
          onClick={() => onOpen('text')}
          className="flex-1 text-left px-4 py-2.5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 text-sm sm:text-base transition-colors"
        >
          Share something with the Caryn Community…
        </button>
      </div>
      <div className="grid grid-cols-3 gap-1 mt-3 pt-3 border-t border-gray-100">
        <EntryChip icon={<Type size={17} className="text-pink-500" />} label="Text" onClick={() => onOpen('text')} />
        <EntryChip icon={<ImageIcon size={17} className="text-pink-500" />} label="Photo" onClick={() => onOpen('photo')} />
        <EntryChip icon={<LinkIcon size={17} className="text-pink-500" />} label="Link" onClick={() => onOpen('link')} />
      </div>
    </div>
  )
}

function EntryChip({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
    >
      {icon}
      {label}
    </button>
  )
}
